import chordPro from '../vendor/js-chordpro';
import type {
  ChordProDirective, ChordProLine, ChordProMetadata, ChordProSection,
  ParsedChordPro, SectionType,
} from './types';

const META_ALIASES: Record<string, string> = {
  t: 'title', ti: 'title', st: 'subtitle', su: 'subtitle', ccli_number: 'ccli', ccli_song: 'ccli',
  duration_seconds: 'duration', x_zoea_duration: 'duration', 'x-zoea-duration': 'duration',
  'x-zoea-youtube': 'youtube', 'x-zoea-spotify': 'spotify', 'x-zoea-drive': 'drive',
};
const METADATA = new Set([
  'title', 'subtitle', 'artist', 'composer', 'lyricist', 'copyright', 'ccli', 'key',
  'tempo', 'time', 'capo', 'album', 'year', 'language', 'tags', 'duration', 'publisher',
  'license', 'source', 'youtube', 'spotify', 'drive',
]);
const START_SECTIONS: Record<string, SectionType> = {
  soc: 'chorus', start_of_chorus: 'chorus', sov: 'verse', start_of_verse: 'verse',
  sob: 'bridge', start_of_bridge: 'bridge', soi: 'intro', start_of_intro: 'intro',
  soo: 'outro', start_of_outro: 'outro', sor: 'repeat', start_of_repeat: 'repeat',
  sot: 'tab', start_of_tab: 'tab', sog: 'grid', start_of_grid: 'grid',
  // Door de importer gebruikt voor Turnaround/Tussenspel en Instrumentaal; de opslag kent deze types ook.
  start_of_interlude: 'interlude', start_of_instrumental: 'instrumental',
};
const END_SECTIONS = new Set([
  'eoc', 'end_of_chorus', 'eov', 'end_of_verse', 'eob', 'end_of_bridge',
  'eoi', 'end_of_intro', 'eoo', 'end_of_outro', 'eor', 'end_of_repeat',
  'eot', 'end_of_tab', 'eog', 'end_of_grid', 'end_of_interlude', 'end_of_instrumental',
]);
const KNOWN_DIRECTIVES = new Set([
  ...METADATA, ...Object.keys(START_SECTIONS), ...END_SECTIONS,
  'comment', 'c', 'chorus', 'verse', 'bridge', 'intro', 'outro', 'repeat', 'tab', 'grid', 'meta',
]);

const normalizeName = (name: string): string => {
  const lower = name.trim().toLowerCase();
  return META_ALIASES[lower] || lower;
};

export const parseChordLine = (raw: string): ChordProLine => {
  const segments: ChordProLine['segments'] = [];
  const pattern = /\[([^\]]+)]/g;
  let chord: string | undefined;
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(raw))) {
    if (match.index > cursor || chord) segments.push({ chord, text: raw.slice(cursor, match.index) });
    chord = match[1].trim();
    cursor = pattern.lastIndex;
  }
  segments.push({ chord, text: raw.slice(cursor) });
  return { raw, segments };
};

// The vendored external parser's grammar only accepts a bare {soc}/
// {start_of_chorus} (and {sot}/{start_of_tab}) with nothing after the name --
// unlike our own parser above, it has no rule for a labelled
// "{start_of_chorus: Chorus}" (a valid, common ChordPro convention, and what
// the PDF/DOCX importer generates for every section). Passed through
// unstripped, that colon+label makes its PEG grammar throw a
// 'Expected "}" but ":" found' syntax error. Since only the label carries
// information this external parse doesn't use anyway, dropping it (not the
// whole line, which would silently lose the chorus/tab formatting toggle
// the external renderer *does* support) keeps that parse succeeding.
const SECTION_DIRECTIVES_WITHOUT_LABEL_SUPPORT = new Set(['soc', 'start_of_chorus', 'sot', 'start_of_tab', 'eot', 'end_of_tab']);

const sourceForExternalParser = (source: string): string => source
  .split(/\r?\n/)
  .filter((line) => {
    const match = line.trim().match(/^\{([^}:]+)(?::[^}]*)?}$/);
    if (!match) return true;
    const name = normalizeName(match[1]);
    // {comment} laten we weg: de externe parser accepteert dat niet binnen een refrein/tab, terwijl het in ChordPro
    // gewoon mag (en de importer het zelf voor arrangementnotities gebruikt). Het externe resultaat dient alleen als
    // extra syntaxcontrole; de opmerkingen zelf worden door onze eigen parser verwerkt.
    return METADATA.has(name) || ['soc', 'start_of_chorus', 'eoc', 'end_of_chorus', 'sot', 'start_of_tab', 'eot', 'end_of_tab'].includes(name);
  })
  .map((line) => {
    const match = line.trim().match(/^\{([^}:]+):[^}]*}$/);
    if (!match) return line;
    const rawName = match[1].trim();
    return SECTION_DIRECTIVES_WITHOUT_LABEL_SUPPORT.has(normalizeName(rawName)) ? `{${rawName}}` : line;
  })
  .join('\n')
  // Een optioneel akkoord [(F#m)] kent de externe parser niet; voor deze extra controle telt het als gewoon [F#m].
  .replace(/\[\(([^\])]+)\)\]/g, '[$1]') + '\n';

export const parseChordPro = (source: string): ParsedChordPro => {
  const metadata: ChordProMetadata = {};
  const directives: ChordProDirective[] = [];
  const sections: ChordProSection[] = [{ type: 'body', lines: [] }];
  const errors: string[] = [];
  const warnings: string[] = [];
  const chords = new Set<string>();
  let current = sections[0];
  let openSection: SectionType | null = null;

  source.replace(/\r\n/g, '\n').split('\n').forEach((raw, index) => {
    const directiveMatch = raw.trim().match(/^\{\s*([^}:\s]+)\s*(?:[:\s]\s*([^}]*))?}$/);
    if (directiveMatch) {
      const originalName = directiveMatch[1].trim().toLowerCase();
      const name = normalizeName(originalName);
      const value = (directiveMatch[2] || '').trim();
      const known = KNOWN_DIRECTIVES.has(originalName) || METADATA.has(name) || originalName.startsWith('x-zoea-') || originalName.startsWith('x_zoea_');
      directives.push({ name: originalName, value, raw, line: index + 1, known });
      if (originalName === 'meta') {
        // {meta: title Naam}: de eerste term is de metadata-naam.
        const meta = value.match(/^(\S+)\s+([\s\S]*)$/);
        const metaName = meta ? normalizeName(meta[1]) : '';
        if (meta && METADATA.has(metaName)) metadata[metaName] = meta[2].trim();
        return;
      }
      if ((originalName === 'comment' || originalName === 'c') && value) {
        // Unlike other directives, a {comment: ...} is a visible in-song
        // annotation (arrangement notes, "guitar only", ...) rather than
        // pure metadata -- it needs to render at its actual position, just
        // styled distinctly from sung lyrics, so it's kept as a real line.
        current.lines.push({ raw: value, segments: [{ text: value }], isAnnotation: true });
        return;
      }
      if (METADATA.has(name)) metadata[name] = value;
      if (originalName.startsWith('x-zoea-') || originalName.startsWith('x_zoea_')) metadata[originalName.replace(/_/g, '-')] = value;
      if (START_SECTIONS[originalName]) {
        if (openSection) warnings.push(`Regel ${index + 1}: sectie ${openSection} was nog niet gesloten.`);
        openSection = START_SECTIONS[originalName];
        current = { type: openSection, label: value || undefined, lines: [] };
        sections.push(current);
      } else if (END_SECTIONS.has(originalName)) {
        if (!openSection) warnings.push(`Regel ${index + 1}: sluitdirective zonder geopende sectie.`);
        openSection = null;
        // A fresh body section, not the original sections[0]: lines that
        // appear after a closed section (e.g. footer text between an
        // instrumental comment and the next verse) must render at their
        // actual position in the song, not get folded into the opening
        // body block.
        current = { type: 'body', lines: [] };
        sections.push(current);
      } else if (['chorus', 'verse', 'bridge', 'intro', 'outro', 'repeat', 'tab', 'grid'].includes(originalName) && !value) {
        current = { type: originalName as SectionType, lines: [] };
        sections.push(current);
      }
      return;
    }
    const line = parseChordLine(raw);
    line.segments.forEach((segment) => segment.chord && chords.add(segment.chord));
    current.lines.push(line);
  });

  if (openSection) warnings.push(`Sectie ${openSection} is niet gesloten.`);
  if (!metadata.title?.trim()) errors.push('Een {title: ...} directive is verplicht.');
  const contentSections = sections.filter((section) => section.lines.some((line) => line.raw.trim()));
  if (!contentSections.length) errors.push('Het lied bevat geen tekst of akkoorden.');

  let externalDocument: unknown;
  try {
    externalDocument = chordPro.parse(sourceForExternalParser(source));
  } catch (error) {
    warnings.push(`Basisparser kon niet alle syntax verwerken: ${error instanceof Error ? error.message.split('\n')[0] : 'onbekende fout'}`);
  }

  const lyrics = contentSections.flatMap((section) => section.lines)
    .map((line) => line.segments.map((segment) => segment.text).join('')).join('\n').trim();
  return {
    source, metadata, directives, sections: contentSections, lyrics, chords: [...chords],
    validation: { valid: errors.length === 0, errors, warnings }, externalDocument,
  };
};
